import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { I18nProvider, useT } from './i18n';
import { ProtectedRoute, SuperAdminRoute, OrgAdminRoute } from './components/ProtectedRoute';

// Public Pages
import LandingPage from './pages/marketing/LandingPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import SsoCallbackPage from './pages/auth/SsoCallbackPage';
import StartTrialPage from './pages/auth/StartTrialPage';
import AcceptInvitePage from './pages/auth/AcceptInvitePage';
import RegistrationSuccessPage from './pages/auth/RegistrationSuccessPage';

// Legal Pages
import KvkkPage from './pages/legal/KvkkPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';
import DistanceSalesPage from './pages/legal/DistanceSalesPage';
import PreInformationPage from './pages/legal/PreInformationPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import OrganizationsPage from './pages/admin/OrganizationsPage';
import UsersPage from './pages/admin/UsersPage';
import LicensesPage from './pages/admin/LicensesPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import DemoRequestsPage from './pages/admin/DemoRequestsPage';
import RegistrationsPage from './pages/admin/RegistrationsPage';
import RegistrationDetailPage from './pages/admin/RegistrationDetailPage';

// Settings Pages
import SettingsPage from './pages/settings/SettingsPage';

// Existing Pages (to be wrapped with protection)
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Signatures from './pages/Signatures';
import Groups from './pages/Groups';
import Disclaimers from './pages/Disclaimers';
import Campaigns from './pages/Campaigns';
import Surveys from './pages/Surveys';
import DeliveryLog from './pages/DeliveryLog';
import BrandAssets from './pages/BrandAssets';
import Alerts from './pages/Alerts';
import Help from './pages/Help';
import MySignature from './pages/MySignature';
import Toast from './components/Toast';
import '@fontsource/barlow/400.css';
import '@fontsource/barlow/500.css';
import '@fontsource/barlow/600.css';
import '@fontsource/barlow-condensed/600.css';
import './styles/variables.css';
import './styles/global.css';
import { AnimatePresence, motion } from 'framer-motion';
import useBrandLogo from './hooks/useBrandLogo';


// Wrapper for existing authenticated pages.
//
// mobileNavOpen lives here, not in App: every route renders its OWN
// AuthenticatedLayout instance, so a navigation remounts it and the drawer
// closes by itself (the sidebar links also call onMobileClose for the
// same-route click case). Above the mobile breakpoint none of this is
// visible — the top bar and overlay are display:none in CSS and the
// sidebar keeps its fixed/collapsible desktop behavior.
const layoutMessages = {
  openMenu: { tr: 'Menüyü Aç', en: 'Open menu' },
  unauthorized: { tr: 'Bu sayfaya erişim yetkiniz yok.', en: 'You are not authorized to view this page.' },
};

function UnauthorizedPage() {
  const { t } = useT(layoutMessages);
  return <div className="p-8 text-center">{t('unauthorized')}</div>;
}

function AuthenticatedLayout({ children, showToast, sidebarCollapsed, setSidebarCollapsed }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { t } = useT(layoutMessages);
  const logo = useBrandLogo('white');

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  return (
    <div className="app-container">
      <header className="mobile-topbar">
        <button
          type="button"
          className="mobile-topbar-menu-btn"
          aria-label={t('openMenu')}
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen(true)}
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <img src={logo.src} alt={logo.alt} className="mobile-topbar-logo" />
      </header>
      {mobileNavOpen && <div className="sidebar-overlay" onClick={() => setMobileNavOpen(false)} />}
      <Sidebar
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <main className={`main-content${sidebarCollapsed ? ' collapsed' : ''}`}>
        {children}
      </main>
    </div>
  );
}

function AnimatedRoutes({ showToast, sidebarCollapsed, setSidebarCollapsed }) {
  const location = useLocation();
  
  // Don't animate on login/SSO-callback pages — the callback in particular
  // is a brief, transient redirect target, not a page a user navigates to.
  if (['/login', '/basla', '/sso/callback'].includes(location.pathname)) {
    return (
      <Routes location={location}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/basla" element={<StartTrialPage />} />
        <Route path="/sso/callback" element={<SsoCallbackPage />} />
      </Routes>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sso/callback" element={<SsoCallbackPage />} />
        <Route path="/basla" element={<StartTrialPage />} />
        {/* Self-service sign-up is the Microsoft-based trial at /basla
            (organizations/signup.py) — old /register links land there. */}
        <Route path="/register" element={<Navigate to="/basla" replace />} />
        <Route path="/davet/:token" element={<AcceptInvitePage />} />
        <Route path="/registration-success" element={<RegistrationSuccessPage />} />
        {/* Plan ve Ödeme is off until card checkout opens (BILLING_CHECKOUT_ENABLED);
            pages/Subscription.jsx and pages/auth/Payment*.jsx are kept for then. */}
        <Route path="/odeme" element={<Navigate to="/settings" replace />} />
        <Route path="/odeme-basarili" element={<Navigate to="/settings" replace />} />
        <Route path="/odeme-basarisiz" element={<Navigate to="/settings" replace />} />
        <Route path="/abonelik" element={<Navigate to="/settings" replace />} />
        <Route path="/kvkk-aydinlatma-metni" element={<KvkkPage />} />
        <Route path="/gizlilik-politikasi" element={<PrivacyPolicyPage />} />
        <Route path="/kullanim-sartlari" element={<TermsOfServicePage />} />
        <Route path="/cerez-politikasi" element={<CookiePolicyPage />} />
        <Route path="/mesafeli-satis-sozlesmesi" element={<DistanceSalesPage />} />
        <Route path="/on-bilgilendirme-formu" element={<PreInformationPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        
        {/* Super Admin Routes */}
        <Route path="/admin/dashboard" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <AdminDashboard />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        <Route path="/admin/organizations" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <OrganizationsPage />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        <Route path="/admin/users" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <UsersPage />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        <Route path="/admin/licenses" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <LicensesPage />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        <Route path="/admin/notifications" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <NotificationsPage />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        <Route path="/admin/audit-log" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <AuditLogPage />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        <Route path="/admin/demo-requests" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <DemoRequestsPage />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        <Route path="/admin/registrations" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <RegistrationsPage />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        <Route path="/admin/registrations/:id" element={
          <SuperAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <RegistrationDetailPage />
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        
        {/* Tenant Routes (Protected) */}
        <Route path="/dashboard" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Dashboard showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />

        {/* Settings Routes */}
        <Route path="/settings" element={
          <ProtectedRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <SettingsPage showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/profile" element={<Navigate to="/settings" replace />} />
        <Route path="/settings/organization" element={<Navigate to="/settings" replace />} />
        
        {/* Other Tenant Routes */}
        <Route path="/users" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Users showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/signatures" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Signatures showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/groups" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Groups showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/disclaimers" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Disclaimers showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/campaigns" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Campaigns showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/surveys" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Surveys showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/delivery-log" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <DeliveryLog />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/brand-assets" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <BrandAssets />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/alerts" element={
          <OrgAdminRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Alerts showToast={showToast} />
              </motion.div>
            </AuthenticatedLayout>
          </OrgAdminRoute>
        } />
        <Route path="/imzam" element={
          <ProtectedRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <MySignature />
              </motion.div>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
        <Route path="/help" element={
          <ProtectedRoute>
            <AuthenticatedLayout showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                <Help />
              </motion.div>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'error' }), 4000);
  };

  return (
    <I18nProvider>
      <Router>
        <AuthProvider>
          <AnimatedRoutes showToast={showToast} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed} />
          <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'error' })} />
        </AuthProvider>
      </Router>
    </I18nProvider>
  );
}

export default App;
